import { motion } from "framer-motion";
import { ShinyButton } from "@/components/ui/ShinyButton";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Boxes, Truck, Landmark } from "lucide-react";

const PERKS = [
  {
    icon: Boxes,
    title: "Wholesale MOQ Pricing",
    copy: "Order in case quantities and unlock per-unit rates built for business buyers.",
    testid: "bulk-deal-card-moq",
  },
  {
    icon: Truck,
    title: "Free Delivery Over ₹1,000",
    copy: "Free standard delivery on qualifying orders across eligible Mumbai delivery zones.",
    testid: "bulk-deal-card-delivery",
  },
  {
    icon: Landmark,
    title: "Pay Within 30 Days",
    copy: "Approved businesses order now and settle later with PAQVERO Credit terms.",
    testid: "bulk-deal-card-credit",
  },
];

export default function BulkDeals() {
  const navigate = useNavigate();
  return (
    <section id="bulk-deals" data-testid="bulk-deals-section" className="relative py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden rounded-3xl border border-line bg-surface2"
        >
          <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[1fr_1.4fr] lg:gap-14">
            <div className="flex flex-col justify-center">
              <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
                <span className="h-px w-10 bg-amber-500" />
                Built For Bulk Buyers
              </p>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl" data-testid="bulk-deals-title">
                The more you stock, the more you save.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-2 sm:text-base">
                PAQVERO is priced for procurement — stepped quantities, transparent per-unit
                rates and business-friendly terms on every order.
              </p>
              <div className="mt-8">
                <ShinyButton
                  data-testid="bulk-deals-cta"
                  onClick={() => navigate("/shop?bulk=1")}
                  className="transition-transform duration-300 hover:scale-[1.03]"
                  contentClassName="px-7 py-3.5 text-sm font-bold"
                >
                  Browse Bulk Deals
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </ShinyButton>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-1">
              {PERKS.map((perk, i) => (
                <motion.div
                  key={perk.testid}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                  data-testid={perk.testid}
                  className="flex items-start gap-4 rounded-2xl border border-line bg-surface p-5 transition-colors duration-300 hover:border-amber-500/30"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-amber-500">
                    <perk.icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-semibold tracking-tight text-1">{perk.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-2">{perk.copy}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
